import axiosInstance from '../api/axiosInstance';

export interface BranchMember {
  userId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: string;
}

export interface BaseCompanyBranch {
  name: string;
  address: string;
  phoneNumber: string;
  isActive: boolean;
  imageUrls: string[];
}

export interface CompanyBranch extends BaseCompanyBranch {
  id: string;
  companyId: string;
  companyName: string;
  members: BranchMember[];
  createdAt: string;
  updatedAt: string;
}

export interface UpdateStockData {
  quantity: number;
  branchId?: string;
  productId?: string;
  price: string | number;
  sellPrice: string;
  isActive: boolean;
}

export interface ProductStockData {
  id: string;
  price: string;
  sellPrice: string;
  quantity: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  branch: CompanyBranch;
  product: {
    id: string;
    name: string;
    sku: string;
    product_url: string;
    warranty: string;
    roomTypes: string[];
    description: string;
    type: string;
    createdAt: string;
    updatedAt: string;
    origin: string | null;
    images: Array<{
      id: string;
      originalUrl: string;
      thumbnailUrl: string;
      sortOrder: number;
      createdAt: string;
      updatedAt: string;
    }>;
    subCategory: {
      id: number;
      categoryId: number;
      createdAt: string;
      updatedAt: string;
      name: {
        uz: string;
        ru: string;
        en: string;
      };
      description: {
        uz: string;
        ru: string;
        en: string;
      };
      category: {
        id: number;
        createdAt: string;
        updatedAt: string;
        name: {
          uz: string;
          ru: string;
          en: string;
        };
        description: {
          uz: string;
          ru: string;
          en: string;
        };
      };
    };
    attributes: Array<{
      id: string;
      value: string;
      key: {
        id: string;
        key: string;
      };
    }>;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface GetBranchesParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export const branchsAPI = {
  getBranches: async (
    params?: GetBranchesParams
  ): Promise<PaginatedResponse<CompanyBranch>> => {
    const response = await axiosInstance.get('/branches', { params });
    return response.data;
  },
  getMyBranches: async (): Promise<CompanyBranch[]> => {
    const response = await axiosInstance.get('/branches/my-branches');
    return response.data?.data ?? response.data;
  },
  getCompanyBranches: async (): Promise<CompanyBranch[]> => {
    const response = await axiosInstance.get('/branches/company');
    return response.data?.data ?? response.data;
  },
  getBranchById: async (branchId: string): Promise<CompanyBranch> => {
    const response = await axiosInstance.get(`/branches/${branchId}`);
    return response.data?.data ?? response.data;
  },
  createBranch: async (data: BaseCompanyBranch): Promise<CompanyBranch> => {
    const response = await axiosInstance.post<CompanyBranch>('/branches', data);
    return response.data;
  },
  updateBranch: async (
    branchId: string,
    data: BaseCompanyBranch
  ): Promise<CompanyBranch> => {
    const response = await axiosInstance.put<CompanyBranch>(
      `/branches/${branchId}`,
      data
    );
    return response.data;
  },
  deleteBranch: async (branchId: string): Promise<void> => {
    await axiosInstance.delete(`/branches/${branchId}`);
  },

  getProductStock: async (
    branchId: string,
    productId: string
  ): Promise<ProductStockData> => {
    const response = await axiosInstance.get(
      `/stock/${branchId}/product/${productId}`
    );
    return response.data?.data ?? response.data;
  },
  createStock: async (data: UpdateStockData): Promise<ProductStockData> => {
    const response = await axiosInstance.post<ProductStockData>(`/stock`, data);
    return response.data;
  },
  updateStock: async (
    branchId: string,
    productId: string,
    data: UpdateStockData
  ): Promise<ProductStockData> => {
    const response = await axiosInstance.patch<ProductStockData>(
      `/stock/branch/${branchId}/product/${productId}`,
      data
    );
    return response.data;
  },
};
